import { memo, useCallback, useEffect, useState } from 'react';
import PhoneInput from 'react-phone-input-2';

import { Checkbox, ConfigProvider, Flex, Form, Input, Select, Table, Tag, Typography } from 'antd';

import lookup from 'country-code-lookup';
import equal from 'fast-deep-equal';
import moment from 'moment-timezone';

import { validatePhone } from '../../../helpers/validate-phone';

import { EMAIL_PLACEHOLDER } from '../../../constants/common';
import messages from '../../../constants/messages';
import { NOTIFICATION_TYPES } from '../../../constants/notification-preferences';

import { useUserCountry } from '../../../hooks/useUserCountry';

import { COLUMN_SIZE } from '../../../components/AppTable/constants';
import CitySelector from '../../../components/CitySelector';

import styles from './styles.module.scss';

const { Title } = Typography;

function ProfileForm({
  editMode = false,
  formRef,
  user,
  user_edit,
  onChange,
  onChangeAddress,
  onUserEditChange,
  setSubmittable
}) {
  const [initialFormValues, setInitialFormValues] = useState({});
  const [countryCode, setCountryCode] = useState('');

  const userDetectedAddress = useUserCountry();

  const [form] = Form.useForm();

  if (formRef) formRef.current = form;

  const values = Form.useWatch([], form);

  useEffect(() => {
    form
      .validateFields({ validateOnly: true })
      .then(() => setSubmittable(true))
      .catch(() => setSubmittable(false));
  }, [form, values]);

  useEffect(() => {
    user?.address?.country
      ? setDefaultCountryCode(user?.address?.country)
      : setCountryCode(userDetectedAddress?.countryCode);
    setInitialFormValues(form.getFieldsValue(true));
  }, []);

  const initFormValues = {
    ...user,
    house_number: user.address?.house_number,
    street: user.address?.street,
    cityCountry: `${user.address.country}, ${user.address.city}`,
    postal_code: user.address?.postal_code
    // address: countries?.find(s => s.value === user?.address?.country)
  };

  function setDefaultCountryCode(country) {
    if (!country) return;

    const countryData = lookup.byCountry(country);

    setCountryCode(countryData?.iso2?.toLowerCase());
  }

  const onUserNotificationPrefChange = (notificationTypeCode, field, checked) => {
    const updatedPreferences = user_edit.notificationPreferences.map(pref => {
      if (pref.notificationType.code === notificationTypeCode) {
        return { ...pref, [field]: checked };
      }
      return pref;
    });

    onUserEditChange(updatedPreferences, 'notificationPreferences');
  };

  const columns = [
    {
      title: '',
      dataIndex: 'name',
      ellipsis: true,
      width: COLUMN_SIZE.XXL,
      render: text => <a>{text}</a>
    },
    {
      title: 'Email',
      dataIndex: 'email_enabled',
      align: 'center',
      ellipsis: true,
      render: (_, record) => (
        <Checkbox
          disabled={!editMode}
          checked={record.email_enabled}
          onChange={e => onUserNotificationPrefChange(record.key, 'email_enabled', e.target.checked)}
          name="email"
        />
      )
    },
    {
      title: 'Push',
      dataIndex: 'push_enabled',
      align: 'center',
      ellipsis: true,
      render: (_, record) => (
        <Checkbox
          disabled={!editMode}
          checked={record.push_enabled}
          onChange={e => onUserNotificationPrefChange(record.key, 'push_enabled', e.target.checked)}
          name="push"
        />
      )
    },
    {
      title: 'SMS',
      dataIndex: 'sms_enabled',
      align: 'center',
      ellipsis: true,
      render: (_, record) => (
        <Checkbox
          disabled={
            !editMode ||
            [
              NOTIFICATION_TYPES.EVENT_REMINDERS,
              NOTIFICATION_TYPES.TASK_REMINDERS,
              NOTIFICATION_TYPES.MEDIUM_PRIORITY_RULES
            ].includes(record.key)
          }
          checked={record.sms_enabled}
          onChange={e => onUserNotificationPrefChange(record.key, 'sms_enabled', e.target.checked)}
          name="sms"
        />
      )
    }
  ];

  const data = (user_edit?.notificationPreferences ?? []).map(preference => ({
    key: preference.notificationType.code,
    name: preference.notificationType.display_name,
    sms_enabled: preference.sms_enabled,
    push_enabled: preference.push_enabled,
    email_enabled: preference.email_enabled
  }));

  const handleValuesChange = changedValues => {
    const [[changedKey, changedValue]] = Object.entries(changedValues);
    const addressFields = ['house_number', 'street', 'postal_code'];

    addressFields.includes(changedKey)
      ? onChangeAddress(changedValue, changedKey)
      : onUserEditChange(changedValue, changedKey);

    markFormDirty();
  };

  function markFormDirty() {
    const current = form.getFieldsValue();
    const isSame = equal(current, initialFormValues);

    onChange?.(!isSame);
  }

  const changeAddress = useCallback((_, option) => {
    const { country, city } = option ?? user?.address;

    onChangeAddress(country, 'country');
    onChangeAddress(city, 'city');
    markFormDirty();
  }, []);

  return (
    <ConfigProvider
      theme={{
        components: {
          Table: {
            headerBg: 'rgba(0, 0, 0, 0.02)'
          }
        }
      }}>
      <Form
        className="d-flex flex-column w-full pt-4"
        layout="vertical"
        form={form}
        requiredMark={editMode}
        initialValues={initFormValues}
        onValuesChange={handleValuesChange}>
        <Flex vertical={true} className="w-full mb-4">
          <Flex align="center" gap="small" vertical={false}>
            <Title level={5}>About You</Title>
            {!!user?.close && <Tag color="error">Close requested</Tag>}
          </Flex>

          <Flex gap="middle" vertical={false} wrap className="w-full">
            <Form.Item
              className={styles['form-item']}
              label="Name"
              name="first_name"
              rules={[{ required: true, message: messages.FIELD_REQUIRED('Name') }]}>
              <Input disabled={!editMode} />
            </Form.Item>
            <Form.Item
              label="Surname"
              name="last_name"
              className={styles['form-item']}
              rules={[{ required: true, message: messages.FIELD_REQUIRED('Surname') }]}>
              <Input disabled={!editMode} />
            </Form.Item>
          </Flex>
        </Flex>

        <Flex vertical={true} className="w-full mb-4">
          <Title level={5}>Home Address</Title>

          <Flex gap="small" vertical={true}>
            <Flex gap="middle" vertical={false} wrap className="w-full">
              <Form.Item
                label="House Number / Name / Farm Name"
                name="house_number"
                className={styles['form-item']}
                rules={[{ required: true, message: messages.FIELD_REQUIRED('House Number') }]}>
                <Input disabled={!editMode} />
              </Form.Item>
              <Form.Item
                label="Street Name"
                name="street"
                className={styles['form-item']}
                rules={[{ required: true, message: messages.FIELD_REQUIRED('Street Name') }]}>
                <Input disabled={!editMode} />
              </Form.Item>
            </Flex>

            <Flex gap="middle" vertical={false} wrap className="w-full">
              <Form.Item
                className={styles['form-item']}
                label="City / Town"
                name="cityCountry"
                rules={[{ required: true, message: messages.FIELD_REQUIRED('City / Town') }]}>
                <CitySelector
                  key="profile"
                  className="flex-fill w-100"
                  defaultValue={`${user?.address?.city}, ${user?.address?.country}`}
                  disabled={!editMode}
                  handleClear={changeAddress}
                  handleSelect={changeAddress}
                />
              </Form.Item>
              <Form.Item
                label="Postal Code"
                name="postal_code"
                className={styles['form-item']}
                rules={[{ required: true, message: messages.FIELD_REQUIRED('Postal Code') }]}>
                <Input disabled={!editMode} />
              </Form.Item>
            </Flex>
          </Flex>
        </Flex>

        <Flex vertical={true} className="w-full mb-4">
          <Title level={5}>Contact</Title>

          <Flex gap="small" vertical={true}>
            <Flex gap="middle" vertical={false} wrap className="w-full">
              <Form.Item
                label="Primary Email"
                name="email"
                className={styles['form-item']}
                rules={[
                  { required: true, message: messages.FIELD_REQUIRED('Primary Email') },
                  { type: 'email', message: messages.FIELD_INVALID('Email') }
                ]}>
                <Input placeholder={EMAIL_PLACEHOLDER} disabled={!editMode} />
              </Form.Item>
              <Form.Item
                label="Backup Email"
                name="backup_email"
                className={styles['form-item']}
                rules={[
                  { required: true, message: messages.FIELD_REQUIRED('Backup Email') },
                  { type: 'email', message: messages.FIELD_INVALID('Email') }
                ]}>
                <Input placeholder={EMAIL_PLACEHOLDER} disabled={!editMode} />
              </Form.Item>
            </Flex>

            <Flex gap="middle" vertical={false} wrap className="w-full">
              <Form.Item
                label="Work Phone"
                name="work_phone"
                className={styles['form-item']}
                rules={[
                  { required: true, message: messages.FIELD_REQUIRED('Work Phone') },
                  validatePhone('Work Phone')
                ]}>
                <PhoneInput
                  inputClass={styles['phone-input']}
                  country={!user_edit.work_phone || user_edit.work_phone === '/' ? countryCode : ''}
                  className="phone-input"
                  disabled={!editMode}
                />
              </Form.Item>
              <Form.Item
                label="Mobile Phone"
                name="notify_phone"
                className={styles['form-item']}
                rules={[
                  { required: true, message: messages.FIELD_REQUIRED('Mobile Phone') },
                  validatePhone('Mobile Phone')
                ]}>
                <PhoneInput
                  inputClass={styles['phone-input']}
                  country={!user?.notify_phone || user?.notify_phone === '/' ? countryCode : ''}
                  className="phone-input"
                  disabled={!editMode}
                />
              </Form.Item>
            </Flex>
          </Flex>
        </Flex>

        <Flex vertical={true} className="w-full mb-4">
          <Title level={5}>Timezone</Title>

          <Flex gap="middle" vertical={false} wrap className="w-full">
            <Form.Item label="Select your timezone" name="timezone" className={styles['form-item']}>
              <Select
                showSearch
                disabled={!editMode}
                className="w-full"
                options={moment.tz.names().map(tz => ({ value: tz, label: tz }))}
                filterOption={(input, option) => option?.label?.toLowerCase().includes(input.toLowerCase())}
              />
            </Form.Item>
          </Flex>
        </Flex>

        <Flex vertical={true} className="w-full mb-2">
          <Title level={5}>Notification Preferences</Title>
          <Table size="small" columns={columns} dataSource={data} pagination={false} scroll={{ x: 500 }} />
        </Flex>
      </Form>
    </ConfigProvider>
  );
}

export default memo(ProfileForm);
